import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ApiCustomerServiceCaseService } from '../../../../../../core/services/api/customer-service/api-customer-service-case.service';
import { ApiCustomerServiceIssueTypeService } from '../../../../../../core/services/api/customer-service/api-customer-service-issue-type.service';
import { ApiSystemSettingsService } from '../../../../../../core/services/api/system-settings/api-system-settings.service';
import { ApiParkingCardListService } from '../../../../../../core/services/api/parking/api-parking-card-list.service';
import { MapperParkingCardListService } from '../../../../../../core/mappers/parking/mapper-parking-card-list.service';
import { ParkingCardUserCandidateDto } from '../../../../../../core/models/parking/parking-card-list.model';
import { MenuPermissionService } from '../../../../../../core/services/menu-permission.service';
import { SiteStateService } from '../../../../../../core/services/site-state.service';

@Component({
  selector: 'app-modal-customer-service-case',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './modal-customer-service-case.component.html',
  styleUrl: './modal-customer-service-case.component.css'
})
export class ModalCustomerServiceCaseComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  // ================= State Variables =================
  formData = {
    subject: '',
    description: '',
    siteId: null as number | null,
    issueTypeId: null as number | null,
    channel: 'phone' as 'phone' | 'walk_in' | 'backoffice',
    cpUserId: null as number | null,
    priority: 'normal' as 'low' | 'normal' | 'high' | 'urgent',
    assignedUserId: null as number | null,
    contactName: '',
    contactPhone: ''
  };

  isSaving = signal(false);
  isVisible = false;
  currentStep = signal<1 | 2>(1);

  // --- Dropdown Lists & Search States ---

  // 1. Sites
  sitesList: Array<{ id: number; name: string }> = [];
  searchSiteText = '';
  isSiteDropdownOpen = false;

  // 2. Issue Types
  issueTypes: Array<{ id: number; name: string; code: string }> = [];
  searchIssueText = '';
  isIssueDropdownOpen = false;

  // 3. Customer (LINE User)
  verifySearchType: 'phone' | 'lineid' | 'uid' | 'cpuserid' = 'phone';
  verifySearchText = '';
  isVerifying = false;
  userVerified = false;
  verifyCandidates: ParkingCardUserCandidateDto[] = [];
  showCandidateList = false;

  // 4. Staff members
  staffList: Array<{ id: number; name: string; username: string }> = [];
  searchStaffText = '';
  isStaffDropdownOpen = false;

  // 5. Channel & Priority Dropdowns
  isChannelDropdownOpen = false;
  isPriorityDropdownOpen = false;

  // 6. Pending images (queue before submit — removable until saved)
  pendingImages: Array<{ fileName: string; previewUrl: string; base64: string }> = [];
  isImageDragOver = false;

  // Display labels mapping
  channelLabels: Record<string, string> = {
    'phone': 'โทรศัพท์ (Phone)',
    'walk_in': 'Walk-in',
    'backoffice': 'ระบบหลังบ้าน (Backoffice)'
  };

  priorityLabels: Record<string, string> = {
    'low': 'ต่ำ (Low)',
    'normal': 'ปกติ (Normal)',
    'high': 'สูง (High)',
    'urgent': 'ด่วนที่สุด (Urgent)'
  };

  private readonly apiCase = inject(ApiCustomerServiceCaseService);
  private readonly apiIssueType = inject(ApiCustomerServiceIssueTypeService);
  private readonly apiSystem = inject(ApiSystemSettingsService);
  private readonly apiParkingCardService = inject(ApiParkingCardListService);
  private readonly mapperParkingCard = inject(MapperParkingCardListService);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly siteState = inject(SiteStateService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  // ================= Normal Functions =================

  ngOnInit(): void {
    setTimeout(() => {
      this.isVisible = true;
    }, 50);

    // Load available sites from SiteStateService
    this.sitesList = this.siteState.sites().map(s => ({ id: s.id, name: s.name }));
    if (this.siteState.selectedSite()) {
      this.formData.siteId = this.siteState.selectedSite()!.id;
    }

    this.loadIssueTypes();
    this.loadStaffList();
  }

  onClose(): void {
    this.isVisible = false;
    setTimeout(() => {
      this.close.emit();
    }, 200);
  }

  nextStep(): void {
    if (!this.userVerified || !this.formData.cpUserId) {
      this.toast.error('กรุณาค้นหาและยืนยันตัวตนผู้ใช้บริการ LINE ที่แจ้งปัญหาก่อน');
      return;
    }
    this.currentStep.set(2);
  }

  prevStep(): void {
    this.currentStep.set(1);
  }

  onImagesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) {
      return;
    }
    this.addImageFiles(Array.from(files));
    input.value = '';
  }

  onImageDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isImageDragOver = true;
  }

  onImageDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isImageDragOver = true;
  }

  onImageDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const current = event.currentTarget as HTMLElement | null;
    const related = event.relatedTarget as Node | null;
    if (current && related && current.contains(related)) {
      return;
    }
    this.isImageDragOver = false;
  }

  onImageDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isImageDragOver = false;
    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }
    this.addImageFiles(Array.from(files));
  }

  removePendingImage(index: number): void {
    this.pendingImages = this.pendingImages.filter((_, i) => i !== index);
  }

  private addImageFiles(files: File[]): void {
    files.forEach((file) => {
      if (!file.type.startsWith('image/')) {
        this.toast.error(`ไฟล์ ${file.name} ไม่ใช่รูปภาพ`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        this.pendingImages = [
          ...this.pendingImages,
          { fileName: file.name, previewUrl: result, base64: result },
        ];
      };
      reader.readAsDataURL(file);
    });
  }

  // --- Issue Type Combobox ---
  get filteredIssueTypes() {
    const q = this.searchIssueText.trim().toLowerCase();
    if (!q) return this.issueTypes;
    return this.issueTypes.filter(opt => opt.name.toLowerCase().includes(q) || opt.code.toLowerCase().includes(q));
  }

  toggleIssueDropdown(): void {
    this.isIssueDropdownOpen = !this.isIssueDropdownOpen;
  }

  selectIssueType(option: { id: number; name: string }): void {
    this.formData.issueTypeId = option.id;
    this.searchIssueText = option.name;
    this.isIssueDropdownOpen = false;
  }

  closeIssueDropdown(): void {
    setTimeout(() => {
      this.isIssueDropdownOpen = false;
      const exactMatch = this.issueTypes.find(
        i => i.name.toLowerCase() === this.searchIssueText.trim().toLowerCase()
      );
      if (exactMatch) {
        this.formData.issueTypeId = exactMatch.id;
        this.searchIssueText = exactMatch.name;
      } else {
        const originalVal = this.issueTypes.find(i => i.id === this.formData.issueTypeId);
        this.searchIssueText = originalVal ? originalVal.name : '';
      }
    }, 200);
  }

  // --- Site Combobox ---
  get filteredSites() {
    const q = this.searchSiteText.trim().toLowerCase();
    if (!q) return this.sitesList;
    return this.sitesList.filter(opt => opt.name.toLowerCase().includes(q));
  }

  toggleSiteDropdown(): void {
    this.isSiteDropdownOpen = !this.isSiteDropdownOpen;
  }

  selectSite(option: { id: number | null; name: string }): void {
    this.formData.siteId = option.id;
    this.searchSiteText = option.name;
    this.isSiteDropdownOpen = false;
  }

  closeSiteDropdown(): void {
    setTimeout(() => {
      this.isSiteDropdownOpen = false;
      if (!this.searchSiteText.trim()) {
        this.formData.siteId = null;
        this.searchSiteText = 'ไม่ผูกลานจอด';
        return;
      }

      const exactMatch = this.sitesList.find(
        i => i.name.toLowerCase() === this.searchSiteText.trim().toLowerCase()
      );
      if (exactMatch) {
        this.formData.siteId = exactMatch.id;
        this.searchSiteText = exactMatch.name;
      } else {
        const originalVal = this.sitesList.find(i => i.id === this.formData.siteId);
        this.searchSiteText = originalVal ? originalVal.name : 'ไม่ผูกลานจอด';
      }
    }, 200);
  }

  // --- Channel Dropdown ---
  toggleChannelDropdown(): void {
    this.isChannelDropdownOpen = !this.isChannelDropdownOpen;
  }

  selectChannel(channel: 'phone' | 'walk_in' | 'backoffice'): void {
    this.formData.channel = channel;
    this.isChannelDropdownOpen = false;
  }

  closeChannelDropdown(): void {
    setTimeout(() => {
      this.isChannelDropdownOpen = false;
    }, 200);
  }

  // --- Priority Dropdown ---
  togglePriorityDropdown(): void {
    this.isPriorityDropdownOpen = !this.isPriorityDropdownOpen;
  }

  selectPriority(priority: 'low' | 'normal' | 'high' | 'urgent'): void {
    this.formData.priority = priority;
    this.isPriorityDropdownOpen = false;
  }

  closePriorityDropdown(): void {
    setTimeout(() => {
      this.isPriorityDropdownOpen = false;
    }, 200);
  }

  // --- Customer Verification (Live API query call) ---
  verifyUser(): void {
    const text = this.verifySearchText.trim();
    if (!text) {
      this.toast.error('กรุณากรอกข้อมูลเพื่อตรวจสอบ');
      return;
    }
    const siteId = this.formData.siteId || this.siteState.selectedSite()?.id || 0;
    const userId = Number(localStorage.getItem('userId') || this.cookieService.get('user_id') || 0);

    this.isVerifying = true;
    this.apiParkingCardService.verifyParkingCardUser(this.verifySearchType, text, siteId, userId).subscribe({
      next: (res) => {
        const mapped = this.mapperParkingCard.mapVerifyUser(res);
        this.verifyCandidates = mapped.candidates;

        if (mapped.candidates.length === 0) {
          this.toast.error('ไม่พบผู้ใช้ที่ตรงกับข้อมูลที่ค้นหา');
          this.userVerified = false;
          this.showCandidateList = false;
        } else if (mapped.requiresSelection) {
          this.showCandidateList = true;
          this.userVerified = false;
          this.toast.info('พบผู้ใช้หลายรายการ กรุณาเลือก');
        } else if (mapped.user) {
          this.applyVerifiedUser(mapped.user);
        }

        this.isVerifying = false;
      },
      error: () => {
        this.toast.error('ตรวจสอบผู้ใช้ไม่สำเร็จ');
        this.isVerifying = false;
      },
    });
  }

  selectVerifiedCandidate(candidate: ParkingCardUserCandidateDto): void {
    this.applyVerifiedUser(candidate);
    this.showCandidateList = false;
  }

  private applyVerifiedUser(user: ParkingCardUserCandidateDto): void {
    this.formData.cpUserId = user.cpUserId ?? null;
    this.formData.contactName = user.fullName;
    this.formData.contactPhone = user.phone;
    this.userVerified = true;
    this.toast.success('ยืนยันตัวตนสำเร็จ');
  }

  clearVerifiedUser(): void {
    this.formData.cpUserId = null;
    this.formData.contactName = '';
    this.formData.contactPhone = '';
    this.userVerified = false;
    this.verifyCandidates = [];
    this.showCandidateList = false;
    this.verifySearchText = '';
  }

  // --- Staff Combobox ---
  get filteredStaff() {
    const q = this.searchStaffText.trim().toLowerCase();
    if (!q) return this.staffList;
    return this.staffList.filter(s => s.name.toLowerCase().includes(q) || s.username.toLowerCase().includes(q));
  }

  toggleStaffDropdown(): void {
    this.isStaffDropdownOpen = !this.isStaffDropdownOpen;
  }

  selectStaff(staff: { id: number; name: string }): void {
    this.formData.assignedUserId = staff.id;
    this.searchStaffText = staff.name;
    this.isStaffDropdownOpen = false;
  }

  closeStaffDropdown(): void {
    setTimeout(() => {
      this.isStaffDropdownOpen = false;
      const exactMatch = this.staffList.find(
        s => s.name.toLowerCase() === this.searchStaffText.trim().toLowerCase()
      );
      if (exactMatch) {
        this.formData.assignedUserId = exactMatch.id;
        this.searchStaffText = exactMatch.name;
      } else {
        const originalVal = this.staffList.find(s => s.id === this.formData.assignedUserId);
        this.searchStaffText = originalVal ? originalVal.name : '';
      }
    }, 200);
  }

  // ================= API Functions =================

  loadIssueTypes(): void {
    this.apiIssueType.getIssueTypeDropdown(true).subscribe({
      next: (res) => {
        const list = res.data || res.Data || res || [];
        this.issueTypes = list.map((i: any) => ({
          id: i.id ?? i.Id,
          name: i.name ?? i.Name,
          code: i.code ?? i.Code
        }));
      },
      error: () => (this.issueTypes = [])
    });
  }

  loadStaffList(): void {
    this.apiSystem.getAccessUsersForTable(1, 200).subscribe({
      next: (res) => {
        const list = res.data || [];
        this.staffList = list.map((u: any) => ({
          id: u.id ?? u.Id,
          username: u.userName ?? u.UserName ?? '',
          name: u.fullName ?? u.FullName ?? u.userName ?? u.UserName ?? '-'
        }));
      },
      error: () => (this.staffList = [])
    });
  }

  onSave(): void {
    // Required Fields Validation
    if (!this.formData.cpUserId || !this.userVerified) {
      this.toast.error('กรุณาค้นหาและยืนยันตัวตนผู้ใช้บริการ LINE ที่แจ้งปัญหาก่อน');
      this.currentStep.set(1);
      return;
    }
    if (!this.formData.subject?.trim()) {
      this.toast.error('กรุณาระบุหัวข้อเรื่องปัญหา');
      return;
    }
    if (!this.formData.description?.trim()) {
      this.toast.error('กรุณาระบุรายละเอียดคำอธิบายปัญหา');
      return;
    }
    if (!this.formData.issueTypeId) {
      this.toast.error('กรุณาระบุประเภทเรื่องปัญหา');
      return;
    }

    const currentUrl = '/customer-service/customer-service-case';
    if (!this.permissionService.canCreate(currentUrl)) {
      this.toast.error('ไม่มีสิทธิ์สร้างเคสแจ้งปัญหา');
      return;
    }

    const userId = Number(localStorage.getItem('userId') || this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(currentUrl) ?? 0;

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    const payload = {
      userId,
      menuId,
      issueTypeId: this.formData.issueTypeId,
      channel: this.formData.channel,
      description: this.formData.description.trim(),
      subject: this.formData.subject.trim(),
      cpUserId: this.formData.cpUserId,
      siteId: this.formData.siteId,
      priority: this.formData.priority,
      assignedUserId: this.formData.assignedUserId,
      contactName: this.formData.contactName.trim() || undefined,
      contactPhone: this.formData.contactPhone.trim() || undefined,
      images: this.pendingImages.length
        ? this.pendingImages.map((img) => ({
            fileName: img.fileName,
            base64: img.base64,
          }))
        : undefined,
    };

    this.isSaving.set(true);
    this.apiCase.createServiceCase(payload).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res.isSuccess) {
          this.toast.success(res.message || 'เปิดเคสแจ้งปัญหาสำเร็จ');
          this.pendingImages = [];
          this.saveSuccess.emit();
          this.onClose();
        } else {
          this.toast.error(res.message || 'เปิดเคสไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
        console.error(err);
      }
    });
  }
}
